/**
 * FeedbackStatus - Atom component for displaying feedback status badges
 */
import React from 'react';

export type FeedbackStatusType = 'new' | 'in_progress' | 'resolved' | 'closed' | 'archived';

interface FeedbackStatusProps {
  status: FeedbackStatusType;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const statusConfig = {
  new: {
    label: 'New',
    colors: 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/20 dark:text-blue-300 dark:border-blue-800',
    icon: '🆕'
  },
  in_progress: {
    label: 'In Progress',
    colors: 'bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-800',
    icon: '🔄'
  },
  resolved: {
    label: 'Resolved',
    colors: 'bg-green-100 text-green-800 border-green-200 dark:bg-green-900/20 dark:text-green-300 dark:border-green-800',
    icon: '✅'
  },
  closed: {
    label: 'Closed',
    colors: 'bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-900/20 dark:text-gray-300 dark:border-gray-800',
    icon: '🔒'
  },
  archived: {
    label: 'Archived',
    colors: 'bg-purple-100 text-purple-800 border-purple-200 dark:bg-purple-900/20 dark:text-purple-300 dark:border-purple-800',
    icon: '📦'
  }
};

const sizeConfig = {
  sm: 'px-2 py-1 text-xs',
  md: 'px-3 py-1.5 text-sm',
  lg: 'px-4 py-2 text-base'
};

export const FeedbackStatus: React.FC<FeedbackStatusProps> = ({ 
  status, 
  size = 'md', 
  className = '' 
}) => {
  const config = statusConfig[status];
  const sizeClasses = sizeConfig[size];
  
  return (
    <span 
      className={`inline-flex items-center gap-1 rounded-full border font-medium ${config.colors} ${sizeClasses} ${className}`}
      role="status"
      aria-label={`Status: ${config.label}`}
    >
      <span className="text-xs" role="img" aria-label={config.label}>
        {config.icon}
      </span>
      <span>{config.label}</span>
    </span>
  );
};

export default FeedbackStatus; 